import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Collapse, Dropdown, Input, Modal, Select, Space, Table, Tag, Typography, message } from "antd";
import { ArrowLeftOutlined, EllipsisOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CopyableText from "components/common/CopyableText";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import { professionSectorService } from "services/professionSectorService";

const { Text } = Typography;

const safeLabel = (sector) =>
  sector?.label || sector?.name || sector?.code || sector?.sectorCode || "-";

export default function HelperProfessionProfilesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState({ totalElements: 0 });
  const [rows, setRows] = useState([]);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sector, setSector] = useState(null);
  const [query, setQuery] = useState("");
  const [sectorOptions, setSectorOptions] = useState([]);
  const [sectorOptionsLoading, setSectorOptionsLoading] = useState(false);

  const helperId = searchParams.get("helperId") || null;

  const parseJsonErrorString = (value) => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith("{")) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  };

  const extractBackendMessage = (err) => {
    const data = err?.response?.data;
    const parsedFromData = parseJsonErrorString(data);
    if (parsedFromData?.message) return parsedFromData.message;
    const parsedFromMessage = parseJsonErrorString(err?.message);
    if (parsedFromMessage?.message) return parsedFromMessage.message;
    if (typeof data === "string" && data.trim()) return data;
    if (data?.message) return data.message;
    if (err?.message) return err.message;
    return "Failed to load profession profiles";
  };

  const isBackendDataError = (err) => {
    const msg = String(extractBackendMessage(err) || "");
    return (
      msg.includes("Cannot deserialize value of type") ||
      msg.includes("JsonToken.START_OBJECT") ||
      msg.includes("java.util.UUID")
    );
  };

  const fetchPage = async (pag = pagination) => {
    setLoading(true);
    setError(null);
    try {
      if (helperId) {
        const res = await olreadiaService.professionProfiles.listForUser(helperId, {
          sector: sector || undefined,
        });
        const list = Array.isArray(res) ? res : [];
        setRows(list);
        setPage({ totalElements: list.length });
      } else {
        const res = await olreadiaService.professionProfiles.list({
          sector: sector || undefined,
          page: (pag.current || 1) - 1,
          size: pag.pageSize || 20,
        });
        setRows(Array.isArray(res?.content) ? res.content : []);
        setPage({ totalElements: res?.totalElements ?? 0 });
      }
    } catch (err) {
      console.error("[helper-professions] list failed", err);
      const backendMessage = extractBackendMessage(err);
      if (isBackendDataError(err)) {
        setError(
          {
            title: "We couldn't load profession profiles",
            message:
              "Some profession profile data is invalid on the server. An admin needs to fix or remove the affected record(s).",
            details: backendMessage,
          },
        );
      } else {
        setError({
          title: "We couldn't load profession profiles",
          message: backendMessage,
          details: backendMessage,
        });
      }
      setRows([]);
      setPage({ totalElements: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helperId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setSectorOptionsLoading(true);
      try {
        const res = await professionSectorService.list();
        const list = Array.isArray(res) ? res : Array.isArray(res?.content) ? res.content : [];
        const options = list
          .map((s) => {
            const code = s?.code || s?.sectorCode;
            if (!code) return null;
            const name = s?.name || s?.label || code;
            return { value: String(code), label: `${name} (${code})` };
          })
          .filter(Boolean);
        if (!cancelled) setSectorOptions(options);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[helper-professions] sector options fetch failed", err);
        if (!cancelled) setSectorOptions([]);
      } finally {
        if (!cancelled) setSectorOptionsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return (rows || []).filter((r) => {
      const helperId = String(r?.userId || r?.helperId || "");
      const profileName = String(r?.profileName || "");
      const name = String(r?.userFullName || "");
      const email = String(r?.userEmail || "");
      const sectorLabel = String(safeLabel(r?.sector) || "");
      const sectorCode = String(r?.sector?.code || r?.sectorCode || "");
      return (
        helperId.toLowerCase().includes(q) ||
        profileName.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        sectorLabel.toLowerCase().includes(q) ||
        sectorCode.toLowerCase().includes(q)
      );
    });
  }, [rows, query]);

  const handleDelete = (record) => {
    if (!record?.id) return;
    const resolvedUserId = record?.userId || record?.helperId || helperId || null;

    Modal.confirm({
      title: "Delete profession profile?",
      content: "This will permanently delete this profession profile.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await olreadiaService.professionProfiles.delete(record.id, {
            userId: resolvedUserId || undefined,
          });
          message.success("Profession profile deleted.");
          await fetchPage({ ...pagination });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[helper-professions] delete failed", err);
          message.error(extractBackendMessage(err));
        }
      },
    });
  };

  const columns = [
    {
      title: "Profile",
      key: "helper",
      render: (_, record) => (
        <div style={{ lineHeight: 1.2 }}>
          <Text strong style={{ display: "block" }} ellipsis>
            {record?.profileName || "Profession Profile"}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CopyableText value={record?.userId || record?.helperId} showTooltip={false} stopPropagation={false} />
          </Text>
        </div>
      ),
    },
    {
      title: "Name",
      key: "name",
      ellipsis: true,
      render: (_, record) => {
        const name = record?.userFullName || "-";
        return <Text ellipsis={{ tooltip: name }}>{name}</Text>;
      },
    },
    {
      title: "Email",
      key: "email",
      ellipsis: true,
      render: (_, record) => {
        const email = record?.userEmail || "-";
        return <Text ellipsis={{ tooltip: email }}>{email}</Text>;
      },
    },
    {
      title: "Sector",
      dataIndex: "sector",
      key: "sector",
      render: (val, record) => {
        const label = safeLabel(val || record?.sector);
        const code = record?.sector?.code || record?.sectorCode || "";
        return (
          <Space direction="vertical" size={0}>
            <Text>{label}</Text>
         
          </Space>
        );
      },
    },
    {
      title: "Profile Type",
      dataIndex: "profileType",
      key: "profileType",
      render: (val) => (val ? <Tag>{String(val).replaceAll("_", " ")}</Tag> : "-"),
    },
    {
      title: "Docs",
      key: "docs",
      align: "center",
      render: (_, r) => {
        const count = Array.isArray(r?.documents) ? r.documents.length : 0;
        return <Tag color={count ? "blue" : "default"}>{count}</Tag>;
      },
    },
    {
      title: "Certs",
      key: "certs",
      align: "center",
      render: (_, r) => {
        const count = Array.isArray(r?.certifications) ? r.certifications.length : 0;
        return <Tag color={count ? "blue" : "default"}>{count}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center",
      render: (_, record) => {
        const resolvedUserId = record?.userId || record?.helperId || helperId || null;
        const menu = {
          items: [
            {
              key: "edit",
              label: "Edit",
              disabled: !record?.id || !resolvedUserId,
              onClick: () => {
                if (!record?.id || !resolvedUserId) return;
                navigate(paths.helperProfessionProfileEditForHelper(resolvedUserId, record.id), {
                  state: { profile: record },
                });
              },
            },
            {
              key: "delete",
              label: "Delete",
              danger: true,
              disabled: !record?.id,
              onClick: () => handleDelete(record),
            },
          ],
        };

        return (
          <div onClick={(e) => e?.stopPropagation?.()}>
            <Dropdown trigger={["click"]} menu={menu}>
              <Button
                type="text"
                icon={<EllipsisOutlined />}
                onClick={(e) => e?.stopPropagation?.()}
                aria-label="Actions"
                title="Actions"
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="id-badge"
          title={helperId ? "Profession Profiles for Helper" : "Helper Profession Profiles"}
          subtitle={
            helperId
              ? `Helper ID: ${helperId}`
              : "Review profession profiles, compliance items, and required documents."
          }
          transform="shrink-2"
          className="flex-1"
        />
        {helperId ? (
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(paths.userHelperProfile(helperId))}
          >
            Back to Helper
          </Button>
        ) : null}
      </div>

      <Card style={{ borderRadius: 10 }}>
        <Space wrap style={{ width: "100%", justifyContent: "space-between", marginBottom: 10 }}>
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Search helper id, profile name, sector..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 340 }}
            />
   
          </Space>
         
        </Space>

        {error ? (
          <Alert
            type="error"
            showIcon
            message={error?.title || "Failed to load"}
            description={
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Text>{error?.message || "Please try again."}</Text>
                {error?.details ? (
                  <Collapse
                    size="small"
                    items={[
                      {
                        key: "details",
                        label: "Technical details",
                        children: (
                          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                            {String(error.details)}
                          </pre>
                        ),
                      },
                    ]}
                  />
                ) : null}
              </Space>
            }
            style={{ marginBottom: 12 }}
            action={
              <Button size="small" onClick={() => fetchPage()}>
                Retry
              </Button>
            }
          />
        ) : null}

        <Table
          rowKey={(r) => r?.id}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          size="small"
          className="custom-table"
          tableLayout="auto"
          scroll={{ x: "max-content" }}
          pagination={
            helperId
              ? false
              : {
                  ...pagination,
                  total: page.totalElements,
                  showSizeChanger: true,
                  pageSizeOptions: ["20", "50", "100"],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }
          }
          onChange={
            helperId
              ? undefined
              : (pag) => {
                  const next = { current: pag.current, pageSize: pag.pageSize };
                  setPagination(next);
                  fetchPage(next);
                }
          }
          onRow={(record) => ({
            onClick: () => {
              if (!record?.id) return;
              navigate(paths.helperProfessionProfileDetails(record.id), { state: { profile: record } });
            },
          })}
          locale={{
            emptyText: loading ? null : "No profession profiles found.",
          }}
        />
      </Card>
    </div>
  );
}
